import { makeStyles } from '@mui/styles';
import Editor, { Monaco, useMonaco } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { Theme } from '@mui/material';
import { useLatest } from '@cloudio-saas/statex';
import { MONACO_LIB_PREFIX } from '../layout/Sidebar/panels/DataSourceView/CodeEditorTypes';
import { useCmdKey } from '../components/Button/useHotKey';

const useStyles = makeStyles((theme: Theme) => ({
  editor: ({ showBorders }: { showBorders: boolean }) => ({
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '& .overflow-guard': {
      border: showBorders ? `1px solid ${theme.palette.divider}` : 0,
    },
  }),
}));

export function useMonacoTypes({
  types,
  typesName,
  dom,
}: {
  types?: string;
  typesName?: string;
  dom?: boolean;
}): void {
  const monaco = useMonaco();
  useEffect(() => {
    if (monaco) {
      monaco.languages.register({ id: 'xml' });
      monaco.languages.html.registerHTMLLanguageService(
        'xml',
        {},
        { documentFormattingEdits: true },
      );
      monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: false,
        noSyntaxValidation: false,
        diagnosticCodesToIgnore: [1375, 1108],
      });
      monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
        jsx: monaco.languages.typescript.JsxEmit.React,
        jsxFactory: 'React.createElement',
        jsxFragmentFactory: 'React.Fragement',
        reactNamespace: 'React',
        target: monaco.languages.typescript.ScriptTarget.ES2020,
        allowNonTsExtensions: true,
        allowSyntheticDefaultImports: true,
        moduleResolution:
          monaco.languages.typescript.ModuleResolutionKind.NodeJs,
        module: monaco.languages.typescript.ModuleKind.ESNext,
        noEmit: true,
        lib: dom
          ? [
              'es5',
              'es2015',
              'es2016',
              'es2017',
              'es2018',
              'es2019',
              'es2020',
              'dom',
            ]
          : ['es5', 'es2015', 'es2016', 'es2017', 'es2018', 'es2019', 'es2020'],
      });
      if (types && typesName) {
        const disposable =
          monaco.languages.typescript.typescriptDefaults.addExtraLib(
            types,
            `${MONACO_LIB_PREFIX}/io${typesName}/index.d.ts`,
          );

        return () => {
          disposable.dispose();
        };
      }
      monaco.languages.register({ id: 'gherkin' });
      monaco.languages.setMonarchTokensProvider('gherkin', {
        tokenizer: {
          root: [
            [/#.*/, 'comment'],
            [/@\w+/, 'keyword'],
            [/\bFeature\b/, 'keyword.feature'],
            [/\bScenario\b/, 'keyword.scenario'],
            [/\bScenario Outline\b/, 'keyword.scenarioOutline'],
            [/\bGiven\b|\bWhen\b|\bThen\b|\bAnd\b|\bBut\b/, 'keyword.step'],
            [/"([^"]*)"/, 'string'],
            [/\bExamples\b/, 'keyword.examples'],
            [/\|.+\|/, 'string'],
            [/\b(\d+)\b/, 'number'],
            [/[a-zA-Z0-9_-]+/, 'identifier'],
          ],
        },
      });

      monaco.languages.setLanguageConfiguration('gherkin', {
        comments: { lineComment: '#' },
        brackets: [
          ['{', '}'],
          ['[', ']'],
          ['(', ')'],
        ],
        autoClosingPairs: [
          { open: '"', close: '"' },
          { open: '{', close: '}' },
          { open: '[', close: ']' },
          { open: '(', close: ')' },
        ],
        surroundingPairs: [
          { open: '"', close: '"' },
          { open: '{', close: '}' },
          { open: '[', close: ']' },
          { open: '(', close: ')' },
        ],
      });
    }
  }, [dom, monaco, types, typesName]);
}

interface UncontrolledCodeInputProps {
  allowJSX?: boolean;
  darkMode: boolean;
  disabled?: boolean;
  height?: number;
  language?: string;
  onChange: (value?: string) => void;
  onMount: (ed: editor.IStandaloneCodeEditor, monaco: Monaco) => void;
  showBorders?: boolean;
  types?: string;
  typesName?: string;
  width?: number | string;
  loadingMessage?: string;
  theme?: string;
  dataTestid?: string;
}

const UncontrolledCodeInput = memo(
  (props: UncontrolledCodeInputProps): React.ReactElement => {
    const {
      allowJSX,
      darkMode,
      disabled,
      height = 400,
      language = 'typescript',
      onChange,
      onMount,
      showBorders = true,
      types,
      typesName,
      width = 400,
      loadingMessage,
      theme,
      dataTestid,
    } = props;
    const classes = useStyles({ showBorders });
    const options: editor.IStandaloneEditorConstructionOptions = {
      extraEditorClassName: classes.editor,
      folding: true,
      lineNumbers: 'on' as const,
      minimap: { enabled: false },
      readOnly: disabled,
      scrollBeyondLastLine: false,
      selectOnLineNumbers: true,
      renderLineHighlight: 'none',
      wordWrap: 'on',
      unicodeHighlight: {
        invisibleCharacters: false,
      },
      // overflowWidgetsDomNode: document.body,
    };

    useMonacoTypes({ types, typesName, dom: allowJSX });

    return (
      <div data-testid={dataTestid}>
        <Editor
          defaultValue=""
          height={height}
          language={language}
          loading={loadingMessage ?? 'Loading code editor...'}
          onChange={onChange}
          onMount={onMount}
          options={options}
          theme={theme ? theme : !darkMode ? 'vs' : 'vs-dark'}
          width={width}
        />
      </div>
    );
  },
);

interface Props {
  allowJSX?: boolean;
  darkMode: boolean;
  disabled?: boolean;
  focused?: boolean;
  height?: number;
  language?: string;
  loadingMessage?: string;
  onChange?: (value: string) => void;
  onMount?: (ed: editor.IStandaloneCodeEditor, monaco: Monaco) => void;
  onSave?: (val: string) => void;
  showBorders?: boolean;
  types?: string;
  typesName?: string;
  value: string | null;
  width?: number | string;
  theme?: string;
  dataTestid?: string;
}

let counter = 1;

const CodeInput = ({
  allowJSX,
  darkMode,
  disabled,
  focused,
  height = 400,
  language = 'typescript',
  loadingMessage,
  onChange,
  onMount,
  onSave,
  showBorders = true,
  types,
  typesName,
  value,
  dataTestid,
  width = 400,
  theme,
}: Props): React.ReactElement => {
  const ref = useRef<editor.IStandaloneCodeEditor | null>(null);
  const valueRef = useRef({ value, focused });
  const latest = useLatest({ onChange, onSave, allowJSX });

  const handleOnChange = useCallback(
    (v = '') => {
      if (valueRef.current.value !== v) {
        valueRef.current.value = v;
        if (latest.current.onChange) latest.current.onChange(v);
      }
    },
    [latest],
  );
  const model = useRef<editor.ITextModel | null>(null);
  useCmdKey({
    hotKey: 's',
    handler: async () => {
      if (ref.current && latest.current.onSave) {
        await ref.current.getAction('editor.action.formatDocument')?.run();
        const val = ref.current.getValue();
        handleOnChange(val);
        latest.current.onSave(val);
      }
    },
    filter: () => !!ref.current && !!latest.current.onSave,
  });
  const editorDidMount = useCallback(
    (ed: editor.IStandaloneCodeEditor, monaco: Monaco) => {
      ref.current = ed;
      // ed.setValue(valueRef.current.value ?? '');

      ed.addCommand(monaco.KeyCode.F5, async () => {
        await ed.getAction('editor.action.formatDocument')?.run();
        const val = ed.getValue();
        handleOnChange(val);
        if (latest.current.onSave) {
          latest.current.onSave(val);
        }
      });
      // if (latest.current.allowJSX) {
      // import ... '@cloudio-saas/datasource-types' is not working without the below code
      if (model.current === null) {
        let ext = '.tsx';
        switch (language) {
          case 'sql':
            ext = '.sql';
            break;
          case 'json':
            ext = '.json';
            break;
        }
        model.current = monaco.editor.createModel(
          valueRef.current.value ?? '',
          language,
          monaco.Uri.file(`file${counter++}.${ext}`),
        );
      }
      ed.setModel(model.current);
      // }
      if (valueRef.current.focused) {
        ed.focus();
      }

      onMount?.(ed, monaco);

      return () => {
        ed.setModel(null);
        model.current?.dispose();
        model.current = null;
      };
    },
    [handleOnChange, language, onMount],
  );

  useEffect(() => {
    return () => {
      model.current?.dispose();
    };
  }, []);

  useEffect(() => {
    if (value !== valueRef.current.value) {
      valueRef.current.value = value;
      if (ref.current) {
        // value changed but the editor is not mounted yet
        ref.current.setValue(value ?? '');
      }
    }
  }, [value]);

  return (
    <UncontrolledCodeInput
      dataTestid="code"
      darkMode={darkMode}
      disabled={disabled}
      height={height}
      language={language}
      onChange={handleOnChange}
      onMount={editorDidMount}
      showBorders={showBorders}
      width={width}
      loadingMessage={loadingMessage}
      types={types}
      typesName={typesName}
      allowJSX={allowJSX}
      theme={theme}
    />
  );
};

export default memo(CodeInput);
